import { useMemo } from 'react'
import set from 'lodash/set'
import lodashGet from 'lodash/get'
import { useTemplateContext } from '@/context/TemplateProvider'
import { DEFAULT_STORE_PRODUCT_IMAGE } from '@siteplus/ai-templates'
import useAIConfig from './useAIConfig'
import useOnboardingContext from './useOnboardingContext'
import {
  templateConfigMappings,
  FALLBACK_TYPES,
  FALLBACK_IMAGE,
} from '@/constants/templateConfigMappings'

const isBlankAccountValue = (v) =>
  v == null || (typeof v === 'string' && v.trim() === '')

/**
 * Generic hook that builds config overrides for any template based on mapping definitions.
 * 
 * @param {string} templateKey - The key matching a template in templateConfigMappings (e.g., 'about')
 * @returns {Object|undefined} - Config overrides object, or undefined if no mappings exist
 */
const useConfigOverrides = (templateKey) => {
  const { template } = useTemplateContext()
  const { get } = useAIConfig()
  const { selectedTemplate } = useOnboardingContext()

  return useMemo(() => {
    const mappings = templateConfigMappings[templateKey]
    
    if (!mappings) {
      return undefined
    }

    const heroImage = template?.sections?.hero?.backgroundImage || FALLBACK_IMAGE
    const overrides = {}
    const templateName = selectedTemplate?.name

    for (const row of mappings) {
      const [configPath, apiPath, fallbackType, staticFallback, forTemplates] = row
      if (Array.isArray(forTemplates) && forTemplates.length > 0) {
        if (!templateName || !forTemplates.includes(templateName)) {
          continue
        }
      }

      let fallback
      
      switch (fallbackType) {
        case FALLBACK_TYPES.HERO_IMAGE:
          fallback = heroImage
          break
        case FALLBACK_TYPES.STORE_PRODUCT_IMAGE:
          fallback = DEFAULT_STORE_PRODUCT_IMAGE
          break
        case FALLBACK_TYPES.STATIC:
          fallback = staticFallback
          break
        default:
          fallback = undefined
      }

      const value = get(apiPath, fallback)
      
      if (value !== undefined && value !== null) {
        set(overrides, configPath, value)
      }
    }

    // `/pages` payloads often only include `home.blocks.accountInfo`, not per-page copies.
    if (templateKey === 'contact_form') {
      const fallbacks = [
        ['info.address.value', 'address.content'],
        ['info.email.value', 'email.content'],
        ['info.phone.value', 'phone.content'],
      ]
      for (const [configPath, suffix] of fallbacks) {
        if (!isBlankAccountValue(lodashGet(overrides, configPath))) continue
        const fromHome = get(`home.blocks.accountInfo.${suffix}`, undefined)
        if (!isBlankAccountValue(fromHome)) set(overrides, configPath, fromHome)
      }
    }

    if (templateKey === 'about') {
      const emailPath = 'contact.email'
      if (isBlankAccountValue(lodashGet(overrides, emailPath))) {
        const fromHome = get('home.blocks.accountInfo.email.content', undefined)
        if (!isBlankAccountValue(fromHome)) set(overrides, emailPath, fromHome)
      }
    }

    return Object.keys(overrides).length > 0 ? overrides : undefined
  }, [templateKey, get, template, selectedTemplate?.name])
}

export default useConfigOverrides
